import { TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { componentKey } from '@entities/search-session';
import { Modal } from '@shared/ui/Modal/Modal';

import { conflictKey, confidenceFactorKey, explanationParams } from './explanations';
import { confidenceKey } from './lib';
import styles from './MatchInsights.module.css';
import type { ConfidenceFactor, ConfidenceFactors } from './types';
import { useExplanationContext } from './useExplanationContext';

interface ConfidenceDialogProps {
  open: boolean;
  onClose: () => void;
  factors: ConfidenceFactors;
  currency: string;
}

/**
 * Модалка «Как рассчитано» (BE-084, MVP_PRD §17).
 *
 * Показывает обе величины раздельно — уверенность в данных объявления и
 * уверенность в персональном соответствии — с их составляющими и списком
 * факторов «повысили / понизили». Разделение принципиально: «плохое объявление»
 * и «нечем оценить сценарий» не должны маскировать друг друга (MATCHING §8.4).
 *
 * Ни одно число здесь не считается: всё приходит из `confidence_factors`,
 * зафиксированных на момент прогона. Пересчёт по текущим данным разошёлся бы с
 * сохранённым баллом и сделал бы объяснение неверным.
 */
export function ConfidenceDialog({ open, onClose, factors, currency }: ConfidenceDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const context = useExplanationContext(currency);

  const factorLine = (factor: ConfidenceFactor) => {
    const key = confidenceFactorKey(factor.code);
    if (!context.exists(key)) return null;
    const Icon = factor.direction === 'up' ? TrendUpIcon : TrendDownIcon;
    return (
      <li
        key={factor.code}
        className={factor.direction === 'up' ? styles.factorUp : styles.factorDown}
      >
        <Icon size={14} weight="bold" aria-hidden="true" />
        <span>
          <span className="visually-hidden">
            {t(`results.detail.factor.${factor.direction}`)}:{' '}
          </span>
          {context.t(key, explanationParams({ code: factor.code, params: factor.params }, context))}
        </span>
      </li>
    );
  };

  const share = (label: string, value: number, weight: string) => (
    <div className={styles.shareRow}>
      <dt>
        {label}
        <span className={styles.shareWeight}>{weight}</span>
      </dt>
      <dd className="tabular">{Math.round(value)}</dd>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId} className={styles.dialog}>
      <h2 id={titleId} className={styles.dialogTitle}>
        {t('results.detail.how')}
      </h2>
      <p className={styles.dialogLead}>{t('results.detail.howLead')}</p>

      <section className={styles.dialogSection}>
        <h3 className={styles.dialogSubtitle}>
          {t('results.card.confidence')}
          <span className={`${styles.dialogScore} tabular`}>{Math.round(factors.match.score)}</span>
          <span className={styles.dialogCategory}>{t(confidenceKey(factors.match.category))}</span>
        </h3>
        <dl className={styles.shares}>
          {share(
            t('results.detail.dataConfidence'),
            factors.data.score,
            t('results.detail.share', { percent: 55 }),
          )}
          {share(
            t('results.detail.richness'),
            factors.match.criteria_richness,
            t('results.detail.share', { percent: 25 }),
          )}
          {share(
            t('results.detail.coverage'),
            factors.match.scoring_coverage,
            t('results.detail.share', { percent: 20 }),
          )}
        </dl>
        {factors.match.inapplicable_components.length > 0 ? (
          <p className={styles.inapplicable}>
            {t('results.detail.inapplicable', {
              components: factors.match.inapplicable_components
                .map((code) => t(componentKey(code)))
                .join(', '),
            })}
          </p>
        ) : null}
        <ul className={styles.factors}>{factors.match.factors.map(factorLine)}</ul>
      </section>

      <section className={styles.dialogSection}>
        <h3 className={styles.dialogSubtitle}>
          {t('results.detail.dataConfidence')}
          <span className={`${styles.dialogScore} tabular`}>{Math.round(factors.data.score)}</span>
          <span className={styles.dialogCategory}>{t(confidenceKey(factors.data.category))}</span>
        </h3>
        <dl className={styles.shares}>
          {share(
            t('results.detail.completeness'),
            factors.data.completeness,
            t('results.detail.share', { percent: 45 }),
          )}
          {share(
            t('results.detail.freshness'),
            factors.data.freshness,
            t('results.detail.share', { percent: 25 }),
          )}
          {share(
            t('results.detail.provenance'),
            factors.data.provenance,
            t('results.detail.share', { percent: 20 }),
          )}
          {share(
            t('results.detail.consistency'),
            factors.data.consistency,
            t('results.detail.share', { percent: 10 }),
          )}
        </dl>
        <ul className={styles.factors}>{factors.data.factors.map(factorLine)}</ul>

        {factors.data.conflicts.length > 0 ? (
          <ul className={styles.conflicts}>
            {factors.data.conflicts.map((code) =>
              context.exists(conflictKey(code)) ? <li key={code}>{t(conflictKey(code))}</li> : null,
            )}
          </ul>
        ) : null}
      </section>

      {/* Уверенность — не вероятность успешной покупки (MVP_PRD §17).
          Оговорка стоит рядом с числами, а не в подвале страницы. */}
      <p className={styles.dialogNote}>{t('results.detail.howNote')}</p>
    </Modal>
  );
}
